import { trackHttp } from './httpBusy'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly payload: unknown

  constructor(status: number, code: string, payload: unknown = null) {
    super(code)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.payload = payload
  }
}

/**
 * REST path. Vite proxies `/api` → the Node server in dev.
 */
export function apiUrl(path: string): string {
  return import.meta.env.DEV ? `/api${path}` : path
}

type ApiInit = RequestInit & {
  token?: string | null
  /** Background poll or analytics. Does not light the menu spinner. */
  quiet?: boolean
}

/**
 * JSON fetch against the same-origin auth API.
 * Counts toward the menu spinner unless `quiet` or the call is left untracked.
 */
export function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const { quiet, ...rest } = init
  const task = request<T>(path, rest)
  return quiet ? task : trackHttp(task)
}

async function request<T>(path: string, init: Omit<ApiInit, 'quiet'>): Promise<T> {
  const { token, headers, ...rest } = init
  const res = await fetch(apiUrl(path), {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(rest.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  })
  let body: { error?: string; user?: unknown; token?: string } = {}
  try {
    body = (await res.json()) as typeof body
  } catch {
    body = {}
  }
  if (!res.ok) {
    throw new ApiError(res.status, body.error || 'network', body)
  }
  return body as T
}
